"use client";

import { useIsMutating, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useEffect, useRef } from "react";
import { fetchGraphQLClient } from "@rmh/data-ops/src/amplify/fetchGraphQLClient";
import { usePathname, useRouter } from "@rmh/i18n";
import { usePatientUserAttributes } from "@rmh/ui/cognito";
import {
  UPDATE_PREFERRED_LANGUAGE_MUTATION_KEY,
  getPreferredLanguageQuery,
  preferredLanguageQueryKey,
  toDisplayLocale,
} from "./preferredLanguage";

/** Aligns the signed-in patient's locale with their Canvas Preferred Language, once per mount. */
export const PreferredLanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const { sub, isImpersonating } = usePatientUserAttributes();
  const isSaving =
    useIsMutating({ mutationKey: UPDATE_PREFERRED_LANGUAGE_MUTATION_KEY }) > 0;
  const reconciled = useRef(false);

  const { data, isSuccess, isFetchedAfterMount } = useQuery({
    queryKey: preferredLanguageQueryKey(sub),
    queryFn: async () => {
      const response = await fetchGraphQLClient(getPreferredLanguageQuery, {
        id: sub as string,
      });
      if (response.errors?.length) throw response.errors;
      return response.data.getClientUser.preferredLanguage ?? null;
    },
    enabled: Boolean(sub) && !isImpersonating,
  });

  useEffect(() => {
    if (reconciled.current) return;
    // The patient's own pick wins over the value read on load.
    if (isSaving) {
      reconciled.current = true;
      return;
    }
    if (!isSuccess || !isFetchedAfterMount) return;
    reconciled.current = true;

    const target = toDisplayLocale(data);
    if (target !== locale) {
      router.replace(`${pathname}${window.location.search}`, {
        locale: target,
      });
    }
  }, [
    data,
    isSuccess,
    isFetchedAfterMount,
    isSaving,
    locale,
    pathname,
    router,
  ]);

  return <>{children}</>;
};
